import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError, forkJoin } from 'rxjs';
import { catchError, tap, map, switchMap } from 'rxjs/operators';
import { VdiRequest, VdiResponse, SOFTWARE_SETS } from '../interface/vdi-request';
// Removed: external workflow interfaces
import { environment } from 'src/environments/environment';
// Removed: external workflow service
import { VdiService } from './vdi.service';
import { TicketService } from './ticket.service';

export interface TestResult {
  testName: string;
  success: boolean;
  message: string;
  data?: any;
  error?: any;
  duration?: number;
  timestamp: string;
}

export interface TestSuite {
  suiteName: string;
  tests: TestResult[];
  overallSuccess: boolean;
  totalTests: number;
  passedTests: number;
  failedTests: number;
  totalDuration: number;
  timestamp: string;
}

export interface SystemStatus {
  component: string;
  status: 'healthy' | 'unhealthy' | 'unknown';
  message: string;
  responseTime?: number;
  details?: any;
}

@Injectable({
  providedIn: 'root'
})
export class TestService {
  private apiUrl = environment.apiEndpoint;
  private logPrefix = '[TEST-Service]';
  
  constructor(
    private http: HttpClient,
    private vdiService: VdiService,
    private ticketService: TicketService
  ) {
    this.log('Test Service initialized');
  }

  /**
   * Run comprehensive system tests
   * @returns Observable<TestSuite>
   */
  runComprehensiveTests(): Observable<TestSuite> {
    this.log('Running comprehensive system tests');
    const startTime = Date.now();
    
    return forkJoin({
      backendTests: this.runBackendApiTests(),
      endToEndTests: this.runEndToEndTests(),
      errorHandlingTests: this.runErrorHandlingTests()
    }).pipe(
      map((results) => {
        const allTests = [
          ...results.backendTests.tests,
          ...results.endToEndTests.tests,
          ...results.errorHandlingTests.tests
        ];
        
        const testSuite: TestSuite = {
          suiteName: 'Comprehensive System Tests',
          tests: allTests,
          overallSuccess: allTests.every(test => test.success),
          totalTests: allTests.length,
          passedTests: allTests.filter(test => test.success).length,
          failedTests: allTests.filter(test => !test.success).length,
          totalDuration: Date.now() - startTime,
          timestamp: new Date().toISOString()
        };
        
        this.log('Comprehensive tests completed', testSuite);
        return testSuite;
      }),
      catchError((error) => {
        this.logError('Comprehensive tests failed', error);
        return of({
          suiteName: 'Comprehensive System Tests',
          tests: [{
            testName: 'Test Suite Execution',
            success: false,
            message: 'Failed to execute test suite',
            error: error.message,
            timestamp: new Date().toISOString()
          }],
          overallSuccess: false,
          totalTests: 1,
          passedTests: 0,
          failedTests: 1,
          totalDuration: Date.now() - startTime,
          timestamp: new Date().toISOString()
        } as TestSuite);
      })
    );
  }

  /**
   * Run backend API tests
   * @returns Observable<TestSuite>
   */
  runBackendApiTests(): Observable<TestSuite> {
    this.log('Running backend API tests');
    const startTime = Date.now();
    
    return forkJoin({
      healthCheck: this.testBackendHealth(),
      categoryTest: this.testCategoryApi(),
      subCategoryTest: this.testSubCategoryApi(),
      ticketTest: this.testTicketApi()
    }).pipe(
      map((results) => {
        const tests = [
          results.healthCheck,
          results.categoryTest,
          results.subCategoryTest,
          results.ticketTest
        ];
        
        return {
          suiteName: 'Backend API Tests',
          tests: tests,
          overallSuccess: tests.every(test => test.success),
          totalTests: tests.length,
          passedTests: tests.filter(test => test.success).length,
          failedTests: tests.filter(test => !test.success).length,
          totalDuration: Date.now() - startTime,
          timestamp: new Date().toISOString()
        } as TestSuite;
      })
    );
  }

  // Removed: runN8nIntegrationTests

  /**
   * Run end-to-end tests
   * @returns Observable<TestSuite>
   */
  runEndToEndTests(): Observable<TestSuite> {
    this.log('Running end-to-end tests');
    const startTime = Date.now();
    
    return forkJoin({
      vdiFlowTest: this.testCompleteVdiFlow(),
      gpuVdiTest: this.testGpuVdiFlow(),
      workflowFallbackTest: this.testWorkflowFallback()
    }).pipe(
      map((results) => {
        const tests = [
          results.vdiFlowTest,
          results.gpuVdiTest,
          results.workflowFallbackTest
        ];
        
        return {
          suiteName: 'End-to-End Tests',
          tests: tests,
          overallSuccess: tests.every(test => test.success),
          totalTests: tests.length,
          passedTests: tests.filter(test => test.success).length,
          failedTests: tests.filter(test => !test.success).length,
          totalDuration: Date.now() - startTime,
          timestamp: new Date().toISOString()
        } as TestSuite;
      })
    );
  }

  /**
   * Run error handling tests
   * @returns Observable<TestSuite>
   */
  runErrorHandlingTests(): Observable<TestSuite> {
    this.log('Running error handling tests');
    const startTime = Date.now();
    
    return forkJoin({
      invalidTokenTest: this.testInvalidTokenHandling(),
      networkErrorTest: this.testNetworkErrorHandling(),
      timeoutTest: this.testTimeoutHandling()
    }).pipe(
      map((results) => {
        const tests = [
          results.invalidTokenTest,
          results.networkErrorTest,
          results.timeoutTest
        ];
        
        return {
          suiteName: 'Error Handling Tests',
          tests: tests,
          overallSuccess: tests.every(test => test.success),
          totalTests: tests.length,
          passedTests: tests.filter(test => test.success).length,
          failedTests: tests.filter(test => !test.success).length,
          totalDuration: Date.now() - startTime,
          timestamp: new Date().toISOString()
        } as TestSuite;
      })
    );
  }

  /**
   * Test backend health
   * @returns Observable<TestResult>
   */
  private testBackendHealth(): Observable<TestResult> {
    const startTime = Date.now();
    
    return this.vdiService.getHealthStatus().pipe(
      map((response) => ({
        testName: 'Backend Health Check',
        success: true,
        message: 'Backend API is healthy',
        data: response,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      })),
      catchError((error) => of({
        testName: 'Backend Health Check',
        success: false,
        message: 'Backend API health check failed',
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      }))
    );
  }

  /**
   * Test category API
   * @returns Observable<TestResult>
   */
  private testCategoryApi(): Observable<TestResult> {
    const startTime = Date.now();
    
    return this.ticketService.getProblemCategory().pipe(
      map((response) => ({
        testName: 'Category API Test',
        success: true,
        message: 'Category API responded successfully',
        data: response,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      })),
      catchError((error) => of({
        testName: 'Category API Test',
        success: false,
        message: 'Category API test failed',
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      }))
    );
  }

  /**
   * Test subcategory API
   * @returns Observable<TestResult>
   */
  private testSubCategoryApi(): Observable<TestResult> {
    const startTime = Date.now();
    
    return this.ticketService.getProblemSubCategory(1).pipe(
      map((response) => ({
        testName: 'SubCategory API Test',
        success: true,
        message: 'SubCategory API responded successfully',
        data: response,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      })),
      catchError((error) => of({
        testName: 'SubCategory API Test',
        success: false,
        message: 'SubCategory API test failed',
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      }))
    );
  }

  /**
   * Test ticket API
   * @returns Observable<TestResult>
   */
  private testTicketApi(): Observable<TestResult> {
    const startTime = Date.now();
    const testTicket = {
      title: 'Test Ticket',
      description: 'Test ticket for API validation',
      category: 1,
      priority: 'medium'
    };
    
    return this.ticketService.saveTicketInfo(testTicket).pipe(
      map((response) => ({
        testName: 'Ticket API Test',
        success: true,
        message: 'Ticket API responded successfully',
        data: response,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      })),
      catchError((error) => of({
        testName: 'Ticket API Test',
        success: false,
        message: 'Ticket API test failed',
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      }))
    );
  }

  // Removed: testN8nConnection

  // Removed: testN8nWorkflow

  // Removed: testN8nVdiWorkflow

  // Removed: testN8nStatus

  /**
   * Test complete VDI flow
   * @returns Observable<TestResult>
   */
  private testCompleteVdiFlow(): Observable<TestResult> {
    const startTime = Date.now();
    const testVdiRequest = this.createTestVdiRequest();
    
    return this.vdiService.submitVdiRequestWithOption(testVdiRequest, false).pipe(
      map((response) => ({
        testName: 'Complete VDI Flow Test',
        success: response.result,
        message: 'Complete VDI flow executed successfully',
        data: response,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      })),
      catchError((error) => of({
        testName: 'Complete VDI Flow Test',
        success: false,
        message: 'Complete VDI flow test failed',
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      }))
    );
  }

  /**
   * Test GPU VDI flow
   * @returns Observable<TestResult>
   */
  private testGpuVdiFlow(): Observable<TestResult> {
    const startTime = Date.now();
    const testVdiRequest = this.createTestVdiRequest(true);
    
    return this.vdiService.submitVdiRequestWithOption(testVdiRequest, true).pipe(
      map((response) => ({
        testName: 'GPU VDI Flow Test',
        success: response.result,
        message: 'GPU VDI flow executed successfully',
        data: response,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      })),
      catchError((error) => of({
        testName: 'GPU VDI Flow Test',
        success: false,
        message: 'GPU VDI flow test failed',
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      }))
    );
  }

  /**
   * Test workflow fallback
   * @returns Observable<TestResult>
   */
  private testWorkflowFallback(): Observable<TestResult> {
    const startTime = Date.now();
    const testVdiRequest = this.createTestVdiRequest();
    
    return this.vdiService.submitVdiRequestViaWorkflow(testVdiRequest, true).pipe(
      map((response) => ({
        testName: 'Workflow Fallback Test',
        success: true,
        message: 'Workflow fallback mechanism working',
        data: response,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      })),
      catchError((error) => of({
        testName: 'Workflow Fallback Test',
        success: false,
        message: 'Workflow fallback test failed',
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      }))
    );
  }

  /**
   * Test invalid token handling
   * @returns Observable<TestResult>
   */
  private testInvalidTokenHandling(): Observable<TestResult> {
    const startTime = Date.now();
    
    return this.vdiService.validateToken('invalid-token').pipe(
      map((isValid) => ({
        testName: 'Invalid Token Handling Test',
        success: !isValid,
        message: isValid ? 'Invalid token was incorrectly validated' : 'Invalid token correctly rejected',
        data: { isValid },
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      })),
      catchError((error) => of({
        testName: 'Invalid Token Handling Test',
        success: true,
        message: 'Invalid token handling working as expected',
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      }))
    );
  }

  /**
   * Test network error handling
   * @returns Observable<TestResult>
   */
  private testNetworkErrorHandling(): Observable<TestResult> {
    const startTime = Date.now();
    
    // Try to call a non-existent endpoint to trigger network error
    return this.http.get('https://non-existent-server.com/api/test').pipe(
      map(() => ({
        testName: 'Network Error Handling Test',
        success: false,
        message: 'Network error was not caught',
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      })),
      catchError((error) => of({
        testName: 'Network Error Handling Test',
        success: true,
        message: 'Network error handling working correctly',
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      }))
    );
  }

  /**
   * Test timeout handling
   * @returns Observable<TestResult>
   */
  private testTimeoutHandling(): Observable<TestResult> {
    const startTime = Date.now();
    
    return of(null).pipe(
      map(() => ({
        testName: 'Timeout Handling Test',
        success: true,
        message: 'Timeout handling mechanism is in place',
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      })),
      catchError((error) => of({
        testName: 'Timeout Handling Test',
        success: false,
        message: 'Timeout handling test failed',
        error: error.message,
        duration: Date.now() - startTime,
        timestamp: new Date().toISOString()
      }))
    );
  }

  /**
   * Get system status
   * @returns Observable<SystemStatus[]>
   */
  getSystemStatus(): Observable<SystemStatus[]> {
    this.log('Getting system status');
    
    return forkJoin({
      backend: this.getBackendStatus(),
      vdi: this.getVdiStatus()
    }).pipe(map((results) => [results.backend, results.vdi]));
  }

  /**
   * Get backend status
   * @returns Observable<SystemStatus>
   */
  private getBackendStatus(): Observable<SystemStatus> {
    const startTime = Date.now();
    
    return this.vdiService.getHealthStatus().pipe(
      map(() => ({
        component: 'Backend API',
        status: 'healthy' as const,
        message: 'Backend API is responding',
        responseTime: Date.now() - startTime
      })),
      catchError((error) => of({
        component: 'Backend API',
        status: 'unhealthy' as const,
        message: 'Backend API is not responding',
        responseTime: Date.now() - startTime,
        details: error.message
      }))
    );
  }

  // Removed: getN8nStatus

  /**
   * Get VDI status
   * @returns Observable<SystemStatus>
   */
  private getVdiStatus(): Observable<SystemStatus> {
    const startTime = Date.now();
    
    return this.vdiService.getHealthStatus().pipe(
      map((status) => ({
        component: 'VDI Service',
        status: 'healthy' as const,
        message: 'VDI service is responding',
        responseTime: Date.now() - startTime,
        details: status
      })),
      catchError((error) => of({
        component: 'VDI Service',
        status: 'unhealthy' as const,
        message: 'VDI service is not available',
        responseTime: Date.now() - startTime,
        details: error.message
      }))
    );
  }

  /**
   * Create test VDI request
   * @param requiresGpu Whether to require GPU
   * @returns VdiRequest
   */
  private createTestVdiRequest(requiresGpu: boolean = false): VdiRequest {
    const now = new Date();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    
    return {
      username: 'test-user',
      access_token: 'test-token-123456789',
      startDateTime: now.toISOString(),
      endDateTime: tomorrow.toISOString(),
      isRepeated: false,
      serviceDescription: 'Test VDI request for validation',
      softwareSet: requiresGpu ? 'adobe-creative' : 'office-365',
      software: requiresGpu ? 'Adobe Photoshop' : 'Microsoft Office',
      gpurequirement: requiresGpu,
      justification: 'Testing the VDI request system',
      remarks: 'Automated test request'
    };
  }

  /**
   * Log general information
   * @param message The log message
   * @param data Optional data to log
   */
  private log(message: string, data?: any): void {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level: 'INFO',
      service: 'TEST',
      message,
      data: data || null
    };
    
    console.log(`${this.logPrefix} ${message}`, data || '');
    
    // Store in local storage for debugging
    this.storeLog(logEntry);
  }

  /**
   * Log error information
   * @param message The error message
   * @param error The error object
   */
  private logError(message: string, error?: any): void {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level: 'ERROR',
      service: 'TEST',
      message,
      error: {
        message: error?.message || 'Unknown error',
        status: error?.status || 'Unknown status',
        url: error?.url || 'Unknown URL'
      }
    };
    
    console.error(`${this.logPrefix} ${message}`, error || '');
    
    // Store in local storage for debugging
    this.storeLog(logEntry);
  }

  /**
   * Store log entry in local storage
   * @param logEntry The log entry to store
   */
  private storeLog(logEntry: any): void {
    try {
      const existingLogs = JSON.parse(localStorage.getItem('test-logs') || '[]');
      existingLogs.push(logEntry);
      
      // Keep only last 100 log entries
      if (existingLogs.length > 100) {
        existingLogs.splice(0, existingLogs.length - 100);
      }
      
      localStorage.setItem('test-logs', JSON.stringify(existingLogs));
    } catch (error) {
      console.error('Failed to store log entry', error);
    }
  }

  /**
   * Get stored logs
   * @returns Array of log entries
   */
  getLogs(): any[] {
    try {
      return JSON.parse(localStorage.getItem('test-logs') || '[]');
    } catch (error) {
      console.error('Failed to retrieve logs', error);
      return [];
    }
  }

  /**
   * Clear stored logs
   */
  clearLogs(): void {
    try {
      localStorage.removeItem('test-logs');
      this.log('Test logs cleared');
    } catch (error) {
      console.error('Failed to clear logs', error);
    }
  }
}
